import { Box, Button, Text, useToast } from "@chakra-ui/react";
import type { LocalDate } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../shared/hooks/useApi";
import {
  PatientDocumentAiReviewProcessQuestionRejectionId,
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientTaskInstanceId,
} from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import {
  useMarkDocumentApproved,
  useMarkDocumentSubmitted,
} from "../care-and-task-management/tasks-v2/actions/documentActions";
import type { DocumentReview } from "./DocumentReviewContent";
import type { SubmitDocumentBody } from "./documentReview.mutations";
import { DOCUMENT_REVIEW_ROOT_QUERY_KEY } from "./documentReview.queries";
import {
  runSequentialWrites,
  SaveAnswersError,
  type AnswerWrite,
} from "./question-types/answerWrites";
import RegenerateFailureDetails from "./RegenerateFailureDetails";
import type { DocumentQuestionComment } from "./ReviewSidePanel";
import { isApprovalStillAllowed } from "./reviewRules";
import { buildReopenRequest, type ReviewSession } from "./useReviewSession";

// Every server action of the review screen. Each one refreshes the review AND the Tasks 2.0
// drawer/list (they share DOCUMENT_REVIEW_ROOT_QUERY_KEY), and reports its outcome in a toast.

/** Calls a mutation-group entry whose path id is only known when the action runs. */
function runMutation<TVariables, TData>(
  options: { mutationFn?: (variables: TVariables) => Promise<TData> },
  variables: TVariables
): Promise<TData> {
  if (options.mutationFn === undefined) {
    throw new Error("Mutation has no mutationFn");
  }
  return options.mutationFn(variables);
}

export function errorText(error: unknown, fallback: string): string {
  const text = formatErrorResponse(error);
  // Network failures carry no server message ("AxiosError: Network Error")
  return text === "" || text.startsWith("AxiosError") ? fallback : text;
}

/**
 * The submitted date the reviewer chose: the document's original one (today on a first
 * submission), or a custom day.
 */
export type SubmitDate = { type: "original" } | { type: "custom"; date: LocalDate };

interface Params {
  documentTypeId: PatientDocumentTypeId;
  review: DocumentReview;
  session: ReviewSession;
  taskInstanceId: PatientTaskInstanceId;
}

export function useReviewActions(params: Params) {
  const { documentTypeId, review, session, taskInstanceId } = params;
  const { mutations, queries } = useApi();
  const queryClient = useQueryClient();
  // At the top: the regenerate toast outlives the review, and at the bottom the AngularJS page
  // footer (z-index 999999) covers toasts once the review is closed.
  const toast = useToast({ position: "top" });

  // Awaited in onSuccess, so a mutation stays pending until the refreshed review is shown. A
  // failed refetch does not fail the mutation (the review keeps its data and shows the error).
  const refreshReviewAndTasks = () =>
    queryClient.invalidateQueries({ queryKey: [DOCUMENT_REVIEW_ROOT_QUERY_KEY] });

  const toastError = (title: string, error: unknown, fallback: string) =>
    toast({
      title,
      description: errorText(error, fallback),
      status: "error",
      duration: 8000,
      isClosable: true,
    });

  // ---- Inline edit ----

  // No refresh per write: saveAnswers refreshes once, after its last write (or its failure).
  const saveAnswerMutation = useMutation(
    mutations.documentReview.saveAnswer(taskInstanceId, documentTypeId)
  );
  const [isSavingAnswers, setIsSavingAnswers] = React.useState(false);
  const [saveError, setSaveError] = React.useState<unknown>(null);

  // Saved edits not submitted yet: a submitted document offers Submit (and asks before closing)
  // while > 0. A successful submit, or PDF regeneration, covers the edits saved before it started.
  const [unsubmittedEdits, setUnsubmittedEdits] = React.useState(0);

  /**
   * Writes the answers one after the other and stops at the first failure: it rejects with a
   * SaveAnswersError naming the writes left (what Retry sends), and the failure shows as
   * `saveError`. Every written question counts as edited, and so does `editedQuestionId` (the
   * block whose editor saved, e.g. the chart of a cell) as soon as one write succeeded. Resolves
   * once the refreshed review is shown.
   */
  const saveAnswers = async (
    writes: AnswerWrite[],
    options: { editedQuestionId?: number } = {}
  ): Promise<void> => {
    setIsSavingAnswers(true);
    setSaveError(null);
    let written = 0;
    try {
      await runSequentialWrites(
        writes,
        (write) => saveAnswerMutation.mutateAsync(write),
        (write) => {
          written += 1;
          session.markEdited(write.questionId);
          if (options.editedQuestionId !== undefined) {
            session.markEdited(options.editedQuestionId);
          }
          setUnsubmittedEdits((count) => count + 1);
        }
      );
    } catch (error) {
      setSaveError(error instanceof SaveAnswersError ? error.failure : error);
      throw error;
    } finally {
      // A failed refetch does not fail the save (the review keeps its data and shows the error)
      if (written > 0) {
        await refreshReviewAndTasks();
      }
      setIsSavingAnswers(false);
    }
  };

  /**
   * A patient record (medications, codes, physician) changed from the editor of `questionId`:
   * like an answer save, it marks the question edited and counts as an edit to submit; the review
   * is refreshed (its answers carry e.g. the live medications and codes).
   */
  const recordEntityChange = (questionId: number) => {
    session.markEdited(questionId);
    setUnsubmittedEdits((count) => count + 1);
    void refreshReviewAndTasks();
  };

  const resetSave = () => {
    setSaveError(null);
    saveAnswerMutation.reset();
  };

  // ---- Reopen / withdraw ----

  const reopenMutation = useMutation({
    mutationFn: (variables: {
      body: ReturnType<typeof buildReopenRequest>;
      scheduledDocId: PatientDocumentScheduleId;
    }) =>
      runMutation(
        mutations.documentReview.reopenWithComments(variables.scheduledDocId),
        variables.body
      ),
    onSuccess: () => refreshReviewAndTasks(),
  });

  const reopen = (callbacks: { onSettled: () => void; onSuccess: () => void }) => {
    if (review.scheduledDocId === null) {
      return;
    }
    reopenMutation.mutate(
      { scheduledDocId: review.scheduledDocId, body: buildReopenRequest(session) },
      {
        onSuccess: () => {
          session.reset();
          callbacks.onSuccess();
          toast({
            title: "Reopened for the RN",
            description: "The RN was notified and can fix the document.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
        },
        // Drafts and the general comment are kept, so nothing typed is lost.
        onError: (error) =>
          toastError(
            "Could not reopen the document",
            error,
            "Something went wrong. Your comments were kept."
          ),
        onSettled: callbacks.onSettled,
      }
    );
  };

  const withdrawMutation = useMutation({
    mutationFn: (variables: {
      rejectionId: PatientDocumentAiReviewProcessQuestionRejectionId;
      scheduledDocId: PatientDocumentScheduleId;
    }) =>
      runMutation(
        mutations.documentReview.withdrawComment(variables.scheduledDocId),
        variables.rejectionId
      ),
    onSuccess: () => refreshReviewAndTasks(),
  });

  const withdraw = (comment: DocumentQuestionComment, onSettled: () => void) => {
    if (review.scheduledDocId === null) {
      return;
    }
    withdrawMutation.mutate(
      { scheduledDocId: review.scheduledDocId, rejectionId: comment.id },
      {
        onSuccess: () => toast({ title: "Comment withdrawn", status: "success", duration: 3000 }),
        onError: (error) =>
          toastError("Could not withdraw the comment", error, "Something went wrong."),
        onSettled,
      }
    );
  };

  // ---- Approve / Mark submitted / Submit (re-checked against a fresh load first) ----

  type ApprovalClick = Parameters<typeof isApprovalStillAllowed>[0];

  const approveMutation = useMarkDocumentApproved();
  const markSubmittedMutation = useMarkDocumentSubmitted();
  const [checkingApproval, setCheckingApproval] = React.useState<ApprovalClick | null>(null);

  /**
   * The review on screen may be stale (the RN resubmitted, another reviewer reopened or approved
   * it). Reload it first and only act when the status is still the one the reviewer saw.
   */
  const runApproval = async (clicked: ApprovalClick, run: () => void) => {
    setCheckingApproval(clicked);
    try {
      const fresh = await queryClient.fetchQuery({
        ...queries.documentReview.review(taskInstanceId, documentTypeId),
        staleTime: 0,
      });
      if (!isApprovalStillAllowed(clicked, review, fresh)) {
        toast({
          title: "This document changed — refreshed",
          description: "Check its current state before approving or marking it submitted.",
          status: "info",
          duration: 6000,
          isClosable: true,
        });
        return;
      }
      run();
    } catch (error) {
      toastError("Could not check the document", error, "Something went wrong. Try again.");
    } finally {
      setCheckingApproval(null);
    }
  };

  const approve = () =>
    runApproval("approve", () => {
      if (review.scheduledDocId === null) {
        return;
      }
      approveMutation.mutate(
        { documentScheduleId: review.scheduledDocId },
        {
          onSuccess: () => toast({ title: "Document approved", status: "success", duration: 3000 }),
          onError: (error) =>
            toastError("Could not approve the document", error, "Something went wrong."),
        }
      );
    });

  const markSubmitted = () =>
    runApproval("markSubmitted", () =>
      markSubmittedMutation.mutate(
        { taskInstanceId, documentTypeId },
        {
          onSuccess: () =>
            toast({ title: "Marked as submitted", status: "success", duration: 3000 }),
          onError: (error) =>
            toastError("Could not mark the document as submitted", error, "Something went wrong."),
        }
      )
    );

  const submitMutation = useMutation({
    mutationFn: (variables: { body: SubmitDocumentBody; coveredEdits: number }) =>
      runMutation(mutations.documentReview.submit(taskInstanceId, documentTypeId), variables.body),
    onSuccess: (_data, variables) => {
      setUnsubmittedEdits((count) => Math.max(0, count - variables.coveredEdits));
      return refreshReviewAndTasks();
    },
  });

  /**
   * Submits the document with its answers (the required-answer check comes first, in the UI). A
   * submitted document is submitted again to apply the agency's edits: like the classic form, it
   * keeps its original submitter, and its original date unless a custom one is chosen.
   */
  const submit = (date: SubmitDate, callbacks: { onSuccess?: () => void } = {}) => {
    const isResubmit = review.isSubmitted;
    const coveredEdits = unsubmittedEdits;
    const submittedDate = date.type === "custom" ? date.date : null;
    return runApproval(isResubmit ? "resubmit" : "submit", () =>
      submitMutation.mutate(
        { body: { keepOriginalSubmissionDate: isResubmit, submittedDate }, coveredEdits },
        {
          onSuccess: () => {
            toast({
              title: "Document submitted",
              description:
                submittedDate !== null
                  ? `Submitted date set to ${dateFormatter.toDate(submittedDate)}`
                  : undefined,
              status: "success",
              duration: 3000,
            });
            callbacks.onSuccess?.();
          },
          onError: (error) =>
            toastError("Could not submit the document", error, "Something went wrong."),
        }
      )
    );
  };

  // ---- Regenerate the PDF (side panel action) ----

  const [isRegeneratingPdf, setIsRegeneratingPdf] = React.useState(false);

  const regeneratePdf = (
    scheduledDocId: PatientDocumentScheduleId,
    coveredEdits: number,
    retryToastId?: string | number
  ) => {
    const loading = {
      title: "Regenerating PDF…",
      description: undefined,
      status: "loading" as const,
      duration: null,
      isClosable: false,
    };
    const toastId = retryToastId ?? toast(loading);
    if (retryToastId !== undefined) {
      toast.update(toastId, loading);
    }
    setIsRegeneratingPdf(true);
    runMutation(mutations.documentReview.regeneratePdf(scheduledDocId), undefined)
      .then(() => {
        toast.update(toastId, {
          title: "PDF updated",
          description: undefined,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setUnsubmittedEdits((count) => Math.max(0, count - coveredEdits));
        void refreshReviewAndTasks();
      })
      .catch((error: unknown) =>
        showRegenerateFailureToast(toastId, error, () =>
          regeneratePdf(scheduledDocId, coveredEdits, toastId)
        )
      )
      .finally(() => setIsRegeneratingPdf(false));
  };

  const showRegenerateFailureToast = (
    toastId: string | number,
    error: unknown,
    onRetry: () => void
  ) => {
    const failure = {
      title: "Failed to regenerate the PDF",
      description: (
        <Box>
          <Text>Your changes are saved, but the PDF still shows the previous version.</Text>
          <RegenerateFailureDetails error={error} />
          <Button
            _hover={{ bg: "red.50" }}
            bg="white"
            color="red.600"
            mt={2}
            size="xs"
            onClick={onRetry}
          >
            Retry
          </Button>
        </Box>
      ),
      status: "error" as const,
      // Stays until retried or dismissed
      duration: null,
      isClosable: true,
    };
    toast.update(toastId, failure);
  };

  /** Side panel action: the PDF then shows every edit saved before it started. */
  const regeneratePdfNow = () => {
    if (review.scheduledDocId !== null) {
      regeneratePdf(review.scheduledDocId, unsubmittedEdits);
    }
  };

  return {
    saveAnswers,
    recordEntityChange,
    saveError,
    isSaving: isSavingAnswers,
    resetSave,
    reopen,
    isReopening: reopenMutation.isPending,
    withdraw,
    isWithdrawing: withdrawMutation.isPending,
    withdrawingRejectionId: withdrawMutation.isPending
      ? withdrawMutation.variables.rejectionId
      : null,
    approve,
    markSubmitted,
    submit,
    isApproving:
      checkingApproval === "approve" ||
      checkingApproval === "markSubmitted" ||
      approveMutation.isPending ||
      markSubmittedMutation.isPending,
    isSubmitting:
      checkingApproval === "submit" || checkingApproval === "resubmit" || submitMutation.isPending,
    regeneratePdfNow,
    isRegeneratingPdf,
    /** Saved edits not submitted yet (a submitted document offers Submit while > 0). */
    unsubmittedEdits,
  };
}
